import { FormControl, FormGroup } from '@angular/forms'
import { AssistanceJournalEntry, EditorAssistance, EditorAssistanceAssignment } from '../../../models/EditorAssistance'
import { PartialWithURID, ValueOfFormControl } from '../../../shared/utils/typeUtils'
import { Client } from 'src/app/models/client'
import { ClientService } from '../../../services/domain/client/client.service'
import { AssistanceStatusesService } from '../../../services/domain/codelists/assistance-statuses.service'
import { firstValueFrom } from 'rxjs'
import { AssistanceStatusName } from '../../../models/assistanceStatus'
import { DateTime } from '../../../models/DateTime/DateTime'
import { RecurrenceUtil } from '../../recurrence/RecurrenceUtil'
import { TimeSegment } from '../../../models/DateTime/TimeSegment'

export interface AssistanceEditorForm extends FormGroup<AssistanceEditorFormControls> {}

export interface AssistanceEditorFormControls {
  urid: FormControl<string>
  clientId: FormControl<number>
  plannedFrom: FormControl<DateTime>
  plannedTo: FormControl<DateTime>
  realizedFrom: FormControl<DateTime>
  realizedTo: FormControl<DateTime>
  addressStart: FormControl<string>
  addressEnd: FormControl<string>
  statusId: FormControl<number>
  keyCoordinatorId: FormControl<number>
  assistantAssignments: FormControl<EditorAssistanceAssignment[]>
  isSentToClient: FormControl<boolean>
  coordinatorNote: FormControl<string>
  shortDescription: FormControl<string>
  detailedDescription: FormControl<string>
  isPaidInternally: FormControl<boolean>
  isNotToBePrimarilyAssigned: FormControl<boolean>
  isLateOrder: FormControl<boolean>
  preferredAssistantGenderId: FormControl<number>
  strongAssistantNeeded: FormControl<boolean>
  advancedAssistantNeeded: FormControl<boolean>
  isSleepover: FormControl<boolean>
  nonClientWork: FormControl<boolean>
  isShowInExchange: FormControl<boolean>
  journalEntries: FormControl<AssistanceJournalEntry[]>
  rrule: FormControl<string>
}

export type AssistanceEditorFormControlValues = {
  [K in keyof AssistanceEditorFormControls]: Extract<AssistanceEditorFormControls[K], FormControl> extends FormControl<
    infer I
  >
    ? I
    : never
}

export type AssistanceEditorControlEnabledConditions = {
  [K in keyof AssistanceEditorFormControls]: AssistanceEditorFormControls[K] extends FormControl<infer I>
    ? () => boolean
    : never
}

export function updateForm(
  form: AssistanceEditorForm,
  assistance: EditorAssistance,
  client: Client,
  emitEvent = false,
) {
  console.log(`AssistanceEditorForm#updateForm`, form, assistance)

  const fields = mapAssistanceToFormFields(assistance, client)

  for (const field in fields) {
    if (fields.hasOwnProperty(field) && form.controls[field].pristine) {
      form.controls[field].patchValue(fields[field], { emitEvent: emitEvent })
    }
  }
}

export type AssistanceFormFields = {
  [K in keyof AssistanceEditorFormControls]: ValueOfFormControl<AssistanceEditorFormControls[K]>
}

/**
 * Retrieves the default values for the given assistance.
 *
 * @param assistance - The assistance to retrieve the default values for.
 * @param clientsService - The service used to retrieve client information.
 * @param statusService - The service used to retrieve assistance status information.
 *
 * @return A promise that resolves with the default values for the given assistance.
 */
export async function getDefaultValues(
  assistance: EditorAssistance,
  clientsService: ClientService,
  statusService: AssistanceStatusesService,
): Promise<AssistanceFormFields> {
  const client = assistance.clientId ? await firstValueFrom(clientsService.getById(assistance.clientId)) : null
  const statusId = assistance.statusId
    ? assistance.statusId
    : (await firstValueFrom(statusService.getByNameWithCache(AssistanceStatusName.PLANNED))).id

  return mapAssistanceToFormFields(assistance, client, statusId)
}

export function mapAssistanceToFormFields(
  assistance: EditorAssistance,
  client?: Client,
  statusId?: number,
): AssistanceFormFields {
  const plannedFrom = assistance.plannedFrom
    ? DateTime.fromDate(assistance.plannedFrom)
    : DateTime.fromSegments(null, TimeSegment.fromDate(assistance.templatePlannedTimeFrom))
  const plannedTo = assistance.plannedTo
    ? DateTime.fromDate(assistance.plannedTo)
    : DateTime.fromSegments(null, TimeSegment.fromDate(assistance.templatePlannedTimeTo))

  return {
    urid: assistance.URID,
    clientId: assistance.clientId,
    plannedFrom,
    plannedTo,
    realizedFrom: DateTime.fromDate(assistance.realizedFrom),
    realizedTo: DateTime.fromDate(assistance.realizedTo),
    addressStart: assistance.addressStart,
    addressEnd: assistance.addressEnd,
    statusId: statusId || assistance.statusId,
    keyCoordinatorId: assistance.keyCoordinatorId ?? client?.keyCoordinatorId,
    assistantAssignments: assistance.assignments,
    isSentToClient: assistance.isSentToClient,
    coordinatorNote: assistance.coordinatorNote,
    shortDescription: assistance.shortDescription,
    detailedDescription: assistance.detailedDescription ?? null,
    isPaidInternally: assistance.isPaidInternally,
    isNotToBePrimarilyAssigned: assistance.isNotToBePrimarilyAssigned,
    isLateOrder: assistance.isLateOrder,
    preferredAssistantGenderId: assistance.preferredAssistantGenderId ?? client?.preferredGenderId,
    strongAssistantNeeded: assistance.strongAssistantNeeded ?? client?.needsStrongAssistant,
    advancedAssistantNeeded: assistance.advancedAssistantNeeded ?? client?.needsAdvancedAssistant,
    isSleepover: assistance.isSleepover,
    nonClientWork: assistance.nonClientWork,
    isShowInExchange: assistance.isShowInExchange,
    journalEntries: assistance.journalEntries ?? [],
    rrule: assistance.rrule,
  }
}

export function mapFormFieldsToAssistance(
  fields: Partial<AssistanceFormFields>,
  urid?: string,
): PartialWithURID<EditorAssistance> {
  const uridParsed = urid ? RecurrenceUtil.parseURID(urid) : null
  const out = {
    urid,
    id: uridParsed?.id,
    statusId: fields.statusId,
    clientId: fields.clientId,
    plannedFrom: fields.plannedFrom?.toDate(null),
    plannedTo: fields.plannedTo?.toDate(null),
    realizedFrom: fields.realizedFrom?.toDate(null),
    realizedTo: fields.realizedTo?.toDate(null),
    addressStart: fields.addressStart,
    addressEnd: fields.addressEnd,
    keyCoordinatorId: fields.keyCoordinatorId,
    shortDescription: fields.shortDescription,
    detailedDescription: fields.detailedDescription,
    coordinatorNote: fields.coordinatorNote,
    isSleepover: fields.isSleepover,
    preferredAssistantGenderId: fields.preferredAssistantGenderId,
    strongAssistantNeeded: fields.strongAssistantNeeded,
    advancedAssistantNeeded: fields.advancedAssistantNeeded,
    isNotToBePrimarilyAssigned: fields.isNotToBePrimarilyAssigned,
    isLateOrder: fields.isLateOrder,
    isPaidInternally: fields.isPaidInternally,
    nonClientWork: fields.nonClientWork,
    isSentToClient: fields.isSentToClient,
    isShowInExchange: fields.isShowInExchange,
    isBilled: false,
    journalEntries: fields.journalEntries,
    rrule: fields.rrule,
    assistantAssignments: fields.assistantAssignments,
  }
  return out
}
